import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { CartCalculationOrderResponse } from '~/api/Catalog';
import { PaymentSystem } from '~/api/Order';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import FreeDeliveryProgressBar from '~/components/FreeDeliveryProgressBar/FreeDeliveryProgressBar';
import Icon from '~/components/Icon/Icon';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import DeliveryDesktopOfferSkeleton from '~/pages/Checkout/DeliveryDesktopOfferSkeleton';
import adyenService from '~/services/Adyen';
import { cartStore, checkoutStore, userStore } from '~/stores';
import type Delivery from '~/stores/CheckoutStore/Delivery';
import { isProductBundle } from '~/types/Product';
import styles from './DeliveryDesktop.module.scss';
import { deliveryCarouselOpts } from './deliveryCarouselConfig';
import { useDelivery } from './useDelivery';

interface DeliveryDesktopProps {
  handleSelectDateModal?: () => void;
  delivery: Delivery;
  slotNumber?: number;
  order: CartCalculationOrderResponse;
}

const DeliveryDesktop = ({
  handleSelectDateModal,
  delivery,
  slotNumber,
  order,
}: DeliveryDesktopProps) => {
  const { t } = useTranslation();
  const { handleClickProduct } = useCategoryAnalytics();
  const { isLoading } = cartStore.calculationProcess;

  const {
    deliveryItems,
    showSelectTimeButton,
    deliveryPrice,
    isDeliveryFree,
    leftUntilFreeDelivery,
    progressBarWidth,
    getSelectedDay,
    openDeliveryModal,
    deliveryTooltip: showDeliveryTooltip,
  } = useDelivery({ delivery, order });

  const selectedDay = getSelectedDay();

  const deliveryTooltip = showDeliveryTooltip && (
    <div onClick={openDeliveryModal} className={styles.deliveryTooltip}>
      <Icon type="info" size={18} />
    </div>
  );

  return (
    <li className={classes(['slot-list__slot', styles.container])}>
      {isLoading ? (
        <DeliveryDesktopOfferSkeleton />
      ) : (
        <>
          <div className="slot-list__slot-info">
            <div className="slot-list__slot-header">
              <div className="slot-list__slot-header-group">
                {!slotNumber ? (
                  <p className="slot-list__slot-type">
                    {t('expressDelivery')} {selectedDay}
                    {deliveryTooltip}
                  </p>
                ) : (
                  <p className="slot-list__slot-type">
                    {selectedDay}
                    {deliveryTooltip}
                  </p>
                )}
              </div>
              {isDeliveryFree ? (
                <p className={styles.deliveryFree}>{t('deliveryFree')}</p>
              ) : (
                <p className="slot-list__slot-fee">
                  {t('deliveryFee')}:&nbsp;
                  {deliveryPrice}
                </p>
              )}
            </div>
            {showSelectTimeButton && (
              <button
                data-button="select-date"
                className={styles.selectTimeBtn}
                disabled={
                  checkoutStore.activePaymentSystem === PaymentSystem.adyen &&
                  adyenService.step === 2
                }
                onClick={handleSelectDateModal}
              >
                {t('selectTime')}
              </button>
            )}
          </div>
          <ul className="slot-list__slot-images">
            <Carousel
              opts={deliveryCarouselOpts}
              dir={userStore.dir}
              key={userStore.dir}
            >
              <CarouselContent>
                {deliveryItems.map((item) => (
                  <CarouselItem
                    className={styles.slide}
                    key={item.previewImageThumb}
                  >
                    <Link
                      data-link="product-card"
                      onClick={handleClickProduct(item)}
                      to={
                        `/p/${item.slug}` +
                        (isProductBundle(item) ? '' : `?sku=${item.sku}`)
                      }
                      state={{
                        ...(isProductBundle(item)
                          ? {}
                          : { offerSku: item.sku }),
                      }}
                    >
                      <div className="slot-list__slot-images-item">
                        <CloudflareResponseImage
                          src={item.previewImageThumb || ''}
                          srcParams={{ width: 80, height: 110 }}
                          srcSetParams={defaultSrcSetParams}
                          alt={item.name}
                          loading="lazy"
                        />
                      </div>
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </ul>
          {!isDeliveryFree && (
            <FreeDeliveryProgressBar
              leftUntilFreeDelivery={leftUntilFreeDelivery}
              cssWidth={progressBarWidth}
            />
          )}
        </>
      )}
    </li>
  );
};

export default observer(DeliveryDesktop);
